import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Loader2, Pencil, Trash2, X, Info, Link2 } from "lucide-react";
import { toast } from "sonner";
import { Header } from "./common";

import { confirmar } from "@/components/ConfirmDialog";
const blank = () => ({ name: "", email: "", kind: "professor", instructor_id: "", document: "", notes: "", course_rules: [] });

/** Financeiro → Recebedores: quem recebe parte das vendas (split do Mercado Pago). */
export default function Recipients() {
  const [list, setList] = useState(null);
  const [courses, setCourses] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => api.get("/admin/finance/recipients").then((r) => setList(r.data)).catch(() => setList([])), []);
  useEffect(() => {
    load();
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
    api.get("/instructors").then((r) => setInstructors(r.data)).catch(() => {});
  }, [load]);

  const set = (k, v) => setDlg((p) => ({ ...p, [k]: v }));
  const pickInstructor = (id) => { const i = instructors.find((x) => x.id === id); setDlg((p) => ({ ...p, instructor_id: id, name: i?.name || p.name, email: i?.email || p.email })); };
  const setRule = (i, patch) => setDlg((p) => ({ ...p, course_rules: p.course_rules.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));
  const save = async () => {
    setSaving(true);
    try {
      const body = { ...dlg, course_rules: dlg.course_rules.filter((r) => r.course_id).map((r) => ({ course_id: r.course_id, percent: parseFloat(String(r.percent).replace(",", ".")) || 0 })) };
      if (body.course_rules.some((r) => !(r.percent > 0 && r.percent < 100))) { setSaving(false); return toast.error("O percentual deve ficar entre 0 e 100."); }
      if (dlg.id) await api.put(`/admin/finance/recipients/${dlg.id}`, body); else await api.post("/admin/finance/recipients", body);
      toast.success("Recebedor salvo."); setDlg(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const remove = async (r) => {
    if (!(await confirmar(`Excluir o recebedor ${r.name}? As próximas vendas deixam de ser divididas com ele.`))) return;
    try { await api.delete(`/admin/finance/recipients/${r.id}`); toast.success("Recebedor excluído."); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5 animate-fade-up">
      <Header title="Recebedores" sub="Professores e parceiros que recebem uma parte das vendas de cada curso."
        right={<Button onClick={() => setDlg(blank())} className="text-white font-semibold" style={{ background: "#1E9E2A" }} data-testid="new-recipient"><Plus className="h-4 w-4 mr-1.5" /> Novo recebedor</Button>} />
      <div className="rounded-xl border border-blue-200 bg-blue-50/70 p-4 text-sm text-slate-700 flex gap-3">
        <Info className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
        <div>Na venda de um curso com recebedor, o Mercado Pago divide o valor na hora: o recebedor fica com o percentual dele e o IEP com o restante (menos a taxa do MP).
          Cada curso pode ter <strong>1 recebedor</strong>. O recebedor precisa ter conta no Mercado Pago e <strong>conectar a conta</strong>. A divisão automática é ativada na próxima etapa.</div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        {list === null ? <div className="p-10 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : list.length === 0 ? (
          <p className="p-10 text-center text-slate-400 text-sm">Nenhum recebedor cadastrado.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {list.map((r) => (
              <div key={r.id} className="p-4 flex flex-wrap items-start gap-4">
                <div className="flex-1 min-w-[220px]">
                  <div className="font-semibold text-slate-800">{r.name} <span className="ml-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{r.kind === "parceiro" ? "Parceiro" : "Professor"}</span></div>
                  <div className="text-xs text-slate-400">{r.email || "sem e-mail"}{r.document ? ` · ${r.document}` : ""}</div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(r.course_rules || []).length === 0 ? <span className="text-xs text-slate-400">Nenhum curso definido</span> : r.course_rules.map((c) => (
                      <span key={c.course_id} className="text-xs rounded-md bg-green-50 text-green-800 border border-green-200 px-2 py-1">{c.course || "curso removido"} · <strong>{c.percent}%</strong></span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 ${r.mp_connected ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}><Link2 className="h-3 w-3" /> {r.mp_connected ? "MP conectado" : "MP não conectado"}</span>
                  <Button variant="ghost" size="sm" onClick={() => setDlg({ ...blank(), ...r, course_rules: (r.course_rules || []).map((c) => ({ course_id: c.course_id, percent: c.percent })) })}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" className="text-red-600" onClick={() => remove(r)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto iep-scroll">
          <DialogHeader><DialogTitle className="font-display">{dlg?.id ? "Editar recebedor" : "Novo recebedor"}</DialogTitle></DialogHeader>
          {dlg && <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Tipo</Label>
                <select value={dlg.kind} onChange={(e) => set("kind", e.target.value)} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
                  <option value="professor">Professor</option><option value="parceiro">Parceiro</option></select></div>
              {dlg.kind === "professor" && <div className="space-y-1.5"><Label>Professor</Label>
                <select value={dlg.instructor_id} onChange={(e) => pickInstructor(e.target.value)} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
                  <option value="">—</option>{instructors.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}</select></div>}
            </div>
            <div className="space-y-1.5"><Label>Nome *</Label><Input value={dlg.name} onChange={(e) => set("name", e.target.value)} data-testid="recipient-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>E-mail</Label><Input value={dlg.email} onChange={(e) => set("email", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>CPF / CNPJ</Label><Input value={dlg.document} onChange={(e) => set("document", e.target.value)} /></div>
            </div>
            <div className="space-y-2">
              <Label>Cursos e percentual do recebedor</Label>
              {dlg.course_rules.map((r, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <select value={r.course_id} onChange={(e) => setRule(i, { course_id: e.target.value })} className="h-10 flex-1 rounded-md border border-slate-200 bg-white px-3 text-sm">
                    <option value="">Escolha o curso</option>{courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}</select>
                  <div className="relative w-24"><Input value={r.percent} onChange={(e) => setRule(i, { percent: e.target.value })} className="pr-7" placeholder="0" /><span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">%</span></div>
                  <button onClick={() => setDlg((p) => ({ ...p, course_rules: p.course_rules.filter((_, j) => j !== i) }))} className="text-slate-300 hover:text-red-500"><X className="h-4 w-4" /></button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => setDlg((p) => ({ ...p, course_rules: [...p.course_rules, { course_id: "", percent: "" }] }))}><Plus className="h-4 w-4 mr-1" /> Curso</Button>
            </div>
            <div className="space-y-1.5"><Label>Observações</Label><Textarea rows={2} value={dlg.notes} onChange={(e) => set("notes", e.target.value)} /></div>
          </div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="text-white" style={{ background: "#1E9E2A" }} data-testid="recipient-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
